import React, { useState, useEffect } from 'react';
import { Users, Crown, UserCheck, User, ChevronDown, ChevronRight, Mail, Phone, Calendar } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';

const MyGroup = ({ user }) => {
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    fetchGroupData();
  }, []);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${getApiUrl()}/api/groups/my-group`, {
        headers: getAuthHeaders(),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setGroupData(result.data);
        // Expand all nodes by default
        const expanded = {};
        if (result.data.groupLeader) {
          expanded[result.data.groupLeader.id] = true;
        }
        result.data.teamLeaders?.forEach(tl => {
          expanded[tl.id] = true;
        });
        setExpandedNodes(expanded);
      } else {
        setError(result.message || 'Failed to fetch group data');
      }
    } catch (err) {
      console.error('Fetch group data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'Group Leader':
        return <Crown className="w-5 h-5 text-amber-500" />;
      case 'Team Leader':
        return <UserCheck className="w-5 h-5 text-primary-500" />;
      case 'Agent':
        return <User className="w-5 h-5 text-gray-500" />;
      default:
        return <User className="w-5 h-5 text-gray-400" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Group Leader':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
      case 'Team Leader':
        return 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border-primary-200 dark:border-primary-800';
      case 'Agent':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-dark-border';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-border';
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const UserCard = ({ member, isCurrentUser, showReportsTo }) => (
    <div className={`bg-white dark:bg-dark-card rounded-lg border ${isCurrentUser ? 'border-primary-300 dark:border-primary-600 ring-2 ring-primary-100 dark:ring-primary-900/30' : 'border-gray-200 dark:border-dark-border'} p-4 hover:shadow-md transition-all duration-200`}>
      <div className="flex items-start space-x-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          isCurrentUser ? 'bg-primary-100' : 'bg-gray-100'
        }`}>
          <span className={`font-semibold ${isCurrentUser ? 'text-primary-700 dark:text-primary-400' : 'text-gray-600 dark:text-dark-muted'}`}>
            {getInitials(member.name)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-text truncate">{member.name}</h4>
            {isCurrentUser && (
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">You</span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{member.accountId || 'No Account ID'}</p>
          <div className="flex items-center mt-1">
            {getRoleIcon(typeof member.role === 'string' ? member.role : member.role?.name)}
            <span className={`ml-1.5 text-xs px-2 py-0.5 rounded-full border ${getRoleBadgeColor(typeof member.role === 'string' ? member.role : member.role?.name)}`}>
              {typeof member.role === 'string' ? member.role : member.role?.name || 'N/A'}
            </span>
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center text-xs text-gray-500">
              <Mail className="w-3 h-3 mr-1.5" />
              <span className="truncate">{member.email}</span>
            </div>
            {showReportsTo && member.reportsTo && (
              <div className="flex items-center text-xs text-gray-500">
                <UserCheck className="w-3 h-3 mr-1.5" />
                <span>Reports to: {member.reportsTo}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const HierarchyNode = ({ member, children, level = 0 }) => {
    const hasChildren = children && children.length > 0;
    const isExpanded = expandedNodes[member.id];
    const isCurrentUser = member.id === user?.id;

    return (
      <div className={`${level > 0 ? 'ml-6 border-l-2 border-gray-200 dark:border-dark-border pl-4' : ''}`}>
        <div className="relative">
          {level > 0 && (
            <div className="absolute -left-4 top-6 w-4 h-0.5 bg-gray-200"></div>
          )}
          <div className="flex items-start space-x-2 mb-3">
            {hasChildren && (
              <button
                onClick={() => toggleNode(member.id)}
                className="mt-4 p-1 rounded hover:bg-gray-100 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
              </button>
            )}
            {!hasChildren && <div className="w-6"></div>}
            <div className="flex-1">
              <UserCard member={member} isCurrentUser={isCurrentUser} showReportsTo={level > 0} />
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="space-y-2">
            {children}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-gray-600 dark:text-dark-muted">Loading group data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchGroupData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!groupData) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <Users className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
        <p className="text-yellow-800 font-medium">You are not assigned to any group</p>
        <p className="text-yellow-600 text-sm mt-1">Please contact your administrator to be assigned to a group.</p>
      </div>
    );
  }

  const totalMembers = 1 + // Group Leader
    (groupData.teamLeaders?.length || 0) +
    (groupData.directAgents?.length || 0) +
    (groupData.teamLeaders?.reduce((sum, tl) => sum + (tl.agents?.length || 0), 0) || 0);

  return (
    <div className="space-y-6">
      {/* Group Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{groupData.groupName}</h2>
            <p className="text-primary-100 mt-1">Your team hierarchy and members</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{totalMembers}</div>
            <div className="text-primary-100 text-sm">Team Members</div>
          </div>
        </div>
        <div className="mt-4 flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <Crown className="w-4 h-4 text-amber-300" />
            <span>1 Group Leader</span>
          </div>
          <div className="flex items-center space-x-2">
            <UserCheck className="w-4 h-4 text-primary-300" />
            <span>{groupData.teamLeaders?.length || 0} Team Leaders</span>
          </div>
          <div className="flex items-center space-x-2">
            <User className="w-4 h-4 text-gray-300" />
            <span>
              {(groupData.directAgents?.length || 0) + 
               (groupData.teamLeaders?.reduce((sum, tl) => sum + (tl.agents?.length || 0), 0) || 0)} Agents
            </span>
          </div>
        </div>
      </div>

      {/* Hierarchy View */}
      <div className="bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border p-6 transition-colors duration-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">Team Hierarchy</h3>
            <p className="text-sm text-gray-500">Visual representation of your group structure</p>
          </div>
          <button
            onClick={() => {
              const allExpanded = {};
              if (groupData.groupLeader) {
                allExpanded[groupData.groupLeader.id] = true;
              }
              groupData.teamLeaders?.forEach(tl => {
                allExpanded[tl.id] = true;
              });
              setExpandedNodes(allExpanded);
            }}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Expand All
          </button>
        </div>

        {/* Group Leader */}
        {groupData.groupLeader && (
          <HierarchyNode member={groupData.groupLeader} level={0}>
            {/* Direct Agents under Group Leader */}
            {groupData.directAgents?.map(agent => (
              <HierarchyNode key={agent.id} member={agent} level={1} />
            ))}
            
            {/* Team Leaders and their Agents */}
            {groupData.teamLeaders?.map(tl => (
              <HierarchyNode key={tl.id} member={tl} level={1}>
                {tl.agents?.map(agent => (
                  <HierarchyNode key={agent.id} member={agent} level={2} />
                ))}
              </HierarchyNode>
            ))}
          </HierarchyNode>
        )}
      </div>

      {/* Members List View */}
      <div className="bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border p-6 transition-colors duration-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">All Members</h3>
        
        {/* Group Leader Section */}
        {groupData.groupLeader && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Crown className="w-4 h-4 text-amber-500 mr-2" />
              Group Leader
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <UserCard 
                member={groupData.groupLeader} 
                isCurrentUser={groupData.groupLeader.id === user?.id}
                showReportsTo={false}
              />
            </div>
          </div>
        )}

        {/* Team Leaders Section */}
        {groupData.teamLeaders && groupData.teamLeaders.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <UserCheck className="w-4 h-4 text-primary-500 mr-2" />
              Team Leaders ({groupData.teamLeaders.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupData.teamLeaders.map(tl => (
                <UserCard 
                  key={tl.id} 
                  member={tl} 
                  isCurrentUser={tl.id === user?.id}
                  showReportsTo={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* Agents Section */}
        {((groupData.directAgents && groupData.directAgents.length > 0) || 
          (groupData.teamLeaders && groupData.teamLeaders.some(tl => tl.agents?.length > 0))) && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <User className="w-4 h-4 text-gray-500 mr-2" />
              Agents ({
                (groupData.directAgents?.length || 0) + 
                (groupData.teamLeaders?.reduce((sum, tl) => sum + (tl.agents?.length || 0), 0) || 0)
              })
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Direct Agents (reporting to GL) */}
              {groupData.directAgents?.map(agent => (
                <UserCard 
                  key={agent.id} 
                  member={agent} 
                  isCurrentUser={agent.id === user?.id}
                  showReportsTo={true}
                />
              ))}
              {/* Agents under Team Leaders */}
              {groupData.teamLeaders?.flatMap(tl => 
                tl.agents?.map(agent => (
                  <UserCard 
                    key={agent.id} 
                    member={agent} 
                    isCurrentUser={agent.id === user?.id}
                    showReportsTo={true}
                  />
                )) || []
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyGroup;









